import * as turf from '@turf/turf';
import * as debug from 'debug';
import * as _ from 'lodash';
import * as moment from 'moment';
import * as numeral from 'numeral';
import * as polyline from 'geojson-polyline';
import mapboxgl from 'mapbox-gl/dist/mapbox-gl.js';
import * as $ from 'jquery';
import { TripService } from './trip-service';

export class DrawingTools {
  log = debug('app:DrawingTools');

  weatherMarkers: any = [];
  routeMarkers: any = [];
  routePreviews: any = [];
  weatherData: any = [];

  constructor(public tripService: TripService) {}

  getMap(): Promise<any> {
    if (window['_map']) {
      return Promise.resolve(window['_map']);
    } else {
      // never resolves, so anything trying to draw when there is no map
      // doesn't throw errors
      // console.error('No map available');
      return new Promise(() => {});
    }
  }
  // redraws the map
  gatherPossibleTripRoutesAndDrawRoutes(opt = {}) {
    /* options = {
      trip: Trip Object,
      count: number,
      recenter: bool
    }
    */
    let trip = _.get(opt, 'trip');
    const count = _.get(opt, 'count', 1);
    let recenter = _.get(opt, 'recenter', true);

    if (!trip) {
      trip = this.tripService.getDisplayedTrip();
    }

    if (!_.get(trip, 'origin')) {
      return this.drawRoutes();
    }

    let opts = { overview: 'full' };

    let simplifyImmediately = false;
    if (this.tripService.info.activeTrip === trip) {
      simplifyImmediately = true;
    }

    this.log('will gather routes for', trip);

    const ps = [];
    let includeOptimizedRoute = false;
    if (_.get(opt, 'optimizeRoute')) {
      includeOptimizedRoute = true;
    }
    this.log('opt: ', opt);
    if (count > 1) {
      if (includeOptimizedRoute) {
        ps.push(this.tripService.google.getDirections(trip, opts));
        ps.push(
          this.tripService.google.getDirections(
            trip,
            _.merge(_.cloneDeep(opts), {
              costing_options: {
                truck: {
                  // extremelly costly to change roads (10 minutes)
                  maneuver_penalty: 60 * 10,
                },
              },
            })
          )
        );
        // AVOID TOLLS
        /*
        // turned off to decrease costs
        Tested Routes
        KC - Topeka @ toll cost of 520 the it avoided the tolls
        KC - Wichita @ toll cost of 520, 3000 it failed @ 4500 it avoied the tolls
        KC - Lancaster PA toll cost of 520 it failed @ 4500 it avoied the tolls
        ps.push(
          this.tripService.google.getDirections(
            trip,
            _.merge(_.cloneDeep(opts), {
              costing_options: {
                truck: {
                  tolls_bias: -1,
                  toll_cost: 4500,
                  toll_penalty: 75,
                },
              },
            })
          )
        );
        */
      } else {
        ps.push(this.tripService.google.getOtherDirections(trip, opts));
      }
      this.log('ps: ', ps);
    } else {
      // now we need to decide which version of routing to do, cause they
      // only want one
      const currentRoute = this.tripService.getActiveRoute();

      if (_.get(currentRoute, 'properties.source') === 'MAPBOX') {
        this.log('gatherPossibleTripRoutesAndDrawRoutes | only getting mapbox');
        ps.push(this.tripService.google.getOtherDirections(trip, opts));
      } else {
        _.set(
          opts,
          'costing_options',
          _.get(currentRoute, 'properties.costingOptions')
        );

        this.log(
          'gatherPossibleTripRoutesAndDrawRoutes | only getting valhalla',
          opts,
          currentRoute
        );

        ps.push(this.tripService.google.getDirections(trip, opts));
      }
    }

    return Promise.all(ps)
      .then(results => {
        this.log('RESULTS from directions', results);
        if (results.length >= 2 && _.size(results[0])) {
          const truckDistance = _.get(
            results[0],
            '0.properties.legs.0.distance.value'
          );

          results[1] = _.filter(results[1], route => {
            const dist = _.get(route, 'properties.legs.0.distance.value'),
              diff = Math.abs(dist - truckDistance);

            return diff > 0;
          });

          results[2] = _.filter(results[2], route => {
            const dist = _.get(route, 'properties.legs.0.distance.value'),
              diff = Math.abs(dist - truckDistance),
              ratio = dist / truckDistance;

            // Either it's more than 5 miles or 1% different
            return ratio > 1.01 || ratio < 0.99 || diff > 5 * 1609.34;
          });
        }

        const final = _.union.apply(_, results);
        _.each(final, (f, i) => {
          _.set(f, 'properties.index', i);
        });
        return final;
      })
      .then(routes => {
        // return this.tripService.google.getDirections(trip, opts).then(routes => {
        this.log('got possible routes', routes);
        this.tripService.routeOptions = routes = _.slice(routes, 0, count);
        this.tripService.activeRouteIndex = 0;

        trip.route = routes[0];

        if (simplifyImmediately) {
          this.tripService.setFullRoute(trip.route);
          trip.route = turf.simplify(trip.route, {
            tolerance: 0.01,
            highQuality: false,
          });
          const encoded = polyline.encode(trip.route);
          _.set(
            trip,
            'route.properties.polyLine',
            'path=enc:' + _.get(encoded, 'geometry.coordinates')
          );
        }

        if (trip === this.tripService.info.activeTrip) {
          this.tripService.updateTrip(trip);
        }
        return this.drawRoutes(recenter);
      });
  }

  private drawRoutes(recenter = true) {
    return new Promise<void>((resolve, reject) => {
      this.getMap().then(async m => {
        await this.tripService.addPlaceLayer();

        const self = this,
          trip = this.tripService.getDisplayedTrip();

        let routes = this.tripService.routeOptions;
        this.log('drawing_tools - drawing routes', routes);

        if (!trip || !routes || !_.size(routes)) {
          try {
            m.getSource('routes').setData({
              type: 'FeatureCollection',
              features: [],
            });
          } catch (e) {}
          try {
            m.getSource('waypoints').setData({
              type: 'FeatureCollection',
              features: [],
            });
          } catch (e) {}
          try {
            _.each(self.routeMarkers, m => {
              m.remove();
            });
          } catch (e) {}
          return;
        }

        // if we are in an active trip, let's try to find the stored
        // detailed route
        if (this.tripService.info.activeTrip) {
          const fullRoute = this.tripService.getFullRoute();

          if (fullRoute) {
            routes = [fullRoute];
          }
        }

        let map: any = m,
          start = _.get(routes, '0.geometry.coordinates.0'),
          end = _.last(_.get(routes, '0.geometry.coordinates')),
          routeData = {
            type: 'FeatureCollection',
            features: _.reverse(routes.slice()),
          },
          wayPointData = {
            type: 'FeatureCollection',
            features: _.map(_.get(trip, 'waypoints' || []), wp => {
              return {
                type: 'Feature',
                properties: wp,
                geometry: {
                  type: 'Point',
                  coordinates: wp.location.coordinates,
                },
              };
            }),
          },
          bbox = turf.bbox(routeData); // finds the required Lat Lon to contain all locations in a box

        this.log('waypoint data', wayPointData, trip);

        try {
          this.log('drawRoutes');
          drawRoutes();
        } catch (e) {
          this.log('map_layers | no need to add layers/sources', e);
          // sometimes there may already be a source.
          // Shouldn't really happen, but not a big deal.
          // We will just override data anyway
          updateInfo();
        }

        addStartEndMarkers();
        this.setActiveRoute(this.tripService.activeRouteIndex);
        this.log('mapOptions', bbox);

        if (recenter) {
          map.fitBounds(bbox, { padding: 80 });
        }
        this.setActiveRoute();
        resolve();

        function updateInfo() {
          try {
            map.getSource('routes').setData(routeData);
            map.getSource('waypoints').setData(wayPointData);

            self.log('map_layers | updating route sources', {
              routes: routeData,
              routeSource: map.getSource('routes'),
            });

            _.each(self.routeMarkers, m => {
              m.remove();
            });
            self.routeMarkers = [];
            drawLayers();
          } catch (e) {
            self.log('Error updateInfo Function: ', e);

            map.addLayer('routes');
            map.addSource('routes');

            map.addLayer('waypoints');
            map.addSource('waypoints');

            map.getSource('routes').setData(routeData);
            map.getSource('waypoints').setData(wayPointData);

            _.each(self.routeMarkers, m => {
              m.remove();
            });
            self.routeMarkers = [];
          }
        }

        // Functions for drawing temporary route
        function drawRoutes() {
          map.addSource('routes', {
            type: 'geojson',
            data: routeData,
          });

          map.addSource('waypoints', {
            type: 'geojson',
            data: wayPointData,
          });

          drawLayers();
        }

        function drawLayers() {
          let before;

          try {
            if (_.includes(map.style._order, 'tunnel-oneway-arrows-minor')) {
              before = 'tunnel-oneway-arrows-minor';
            } else if (_.includes(map.style._order, 'tunnel-path')) {
              before = 'tunnel-path';
            }
          } catch (e) {}

          if (!map.getLayer('routes')) {
            if (before) {
              map.addLayer(
                {
                  id: 'routes',
                  source: 'routes',
                  type: 'line',
                  layout: {},
                  paint: {
                    'line-width': {
                      stops: [
                        [0, 6],
                        [5.9, 6],
                        [6, 7],
                        [12, 17],
                      ],
                    },
                  },
                },
                before
              );
            } else {
              map.addLayer(
                {
                  id: 'routes',
                  source: 'routes',
                  type: 'line',
                  layout: {},
                  paint: {
                    'line-width': {
                      stops: [
                        [0, 6],
                        [5.9, 6],
                        [6, 7],
                        [12, 17],
                      ],
                    },
                  },
                },
                'ALL_PLACES'
              );
            }
          }

          if (!map.getLayer('waypoints')) {
            map.addLayer(
              {
                id: 'waypoints',
                source: 'waypoints',
                type: 'circle',
                layout: {},
                paint: {
                  'circle-color': '#189d6b',
                  'circle-radius': {
                    type: 'exponential',
                    base: 10,
                    stops: [
                      [0, 10],
                      [16, 17],
                    ],
                  },
                },
              },
              'ALL_PLACES'
            );
          }
        }

        function addStartEndMarkers() {
          const startMarker = document.createElement('span'),
            endMarker = document.createElement('span');

          startMarker.innerHTML = `
          <i class="fas fa-map-marker-alt fa-2x" text-color="success"></i>
        `;

          endMarker.innerHTML = `
          <i class="fas fa-map-marker-alt fa-2x" text-color="danger"></i>
        `;

          self.routeMarkers.push(
            new mapboxgl.Marker({
              element: startMarker,
              anchor: 'bottom',
            })
              .setLngLat(start)
              .addTo(map)
          );

          self.routeMarkers.push(
            new mapboxgl.Marker({
              element: endMarker,
              anchor: 'bottom',
            })
              .setLngLat(end)
              .addTo(map)
          );
        }
      });
    });
  }

  setActiveRoute(ri?) {
    this.tripService.activeRouteIndex = ri || 0;
    this.log('setActiveRoute', this.tripService.activeRouteIndex);
    this.getMap().then(m => {
      let map = m,
        routeIndex = this.tripService.activeRouteIndex || 0;

      map.setPaintProperty('routes', 'line-color', {
        type: 'categorical',
        property: 'index',
        default: '#666',
        stops: [[routeIndex, '#378ff8']],
      });

      map.setPaintProperty('routes', 'line-opacity', {
        type: 'categorical',
        property: 'index',
        default: 0.3,
        stops: [[routeIndex, 1]],
      });

      map.setFilter('routes', null);

      this.tripService.emit(
        'setActiveRoute',
        this.tripService.activeRouteIndex
      );
    });
  }

  getCurrentZoom() {
    return new Promise((resolve, reject) => {
      this.getMap().then(m => {
        resolve(m.getZoom());
      });
    });
  }

  hideWeather() {
    _.each(this.weatherMarkers, m => {
      $(m._element).hide();
    });
  }

  showWeather() {
    _.each(this.weatherMarkers, m => {
      $(m._element).show();
    });
  }

  private removeWeather() {
    _.each(this.weatherMarkers, m => {
      m.remove();
    });
    this.weatherMarkers = [];
  }

  drawWeather(opts?) {
    return new Promise((resolve, reject) => {
      this.getMap().then(map => {
        let box;

        this.removeWeather();

        const trip = this.tripService.getDisplayedTrip(),
          points = [
            _.get(trip, 'origin.location.coordinates'),
            _.get(trip, 'destination.location.coordinates'),
          ];
        _.each(trip.waypoints, wp => {
          points.push(_.get(wp, 'location.coordinates'));
        });

        const line = turf.lineString(points),
          bbox = turf.bbox(line),
          area = turf.area(turf.bboxPolygon(bbox)) * 3.86102e-7;

        this.log('weather | route area sq miles', area);
        bbox[0] -= 2;
        bbox[1] -= 2;
        bbox[2] += 2;
        bbox[3] += 2;
        box = bbox.join(',') + (area < 100 ? ',12' : ',9');

        $.ajax({
          url: 'https://pro.openweathermap.org/data/2.5/box/city',
          method: 'GET',
          dataType: 'json',
          data: {
            bbox: box,
            appid: '3abbb8358b57cb42b73f83417e96b36d',
          },
        }).then(res => {
          _.each(res.list, item => {
            const id = _.get(item, 'weather.0.id'),
              firstDigit = String(id)[0],
              lookup = {
                6: 100, // snow
                2: 200, // thunderstorm
                5: 300, // rain
                3: 400, // drizzle
                7: 500, // atmosphere
                8: 600, // clear/clouds
              };

            item.severity =
              (lookup[firstDigit] || 600) +
              (99 - parseFloat(String(id).slice(1)));
          });

          this.weatherData = _.sortBy(res.list, 'severity');

          this.log(
            'weather | got weather',
            _.size(this.weatherData),
            this.weatherData
          );

          if (_.get(opts, 'initOnly')) {
            // drawing the weather then hiding on init
            this.showWeatherDataOnRoute().then(() => {
              this.hideWeather();
            });
          } else {
            return this.showWeatherDataOnRoute();
          }
        });
      });
    });
  }

  private showWeatherDataOnRoute() {
    return this.getMap().then(map => {
      this.removeWeather();

      let maxDistance = 200;
      let list = this.weatherData;

      // First, let's find how many are close enough to the route(s)

      try {
        const routeLines = _.map(this.tripService.routeOptions, ro => {
          return turf.simplify(ro, {
            tolerance: 0.01,
            highQuality: false,
          });
        });

        list = _.filter(list, d => {
          let closeEnough = false;

          _.each(routeLines, line => {
            const coords = [_.get(d, 'coord.Lon'), _.get(d, 'coord.Lat')],
              pt = turf.point(coords),
              distance = turf.pointToLineDistance(pt, line, {
                units: 'miles',
              });

            if (distance < 25) {
              closeEnough = true;
              return false;
            }
          });

          return closeEnough;
        });

        this.log(
          'weather | %d icons are close enough to the route',
          _.size(list)
        );

        maxDistance = this.distanceBetweenWeatherIcons(
          _.head(routeLines),
          maxDistance
        );
        this.log('weather | maxDistance: ', maxDistance);
      } catch (e) {
        console.error(e);
      }

      // Now, we make sure two points are too close to each other

      const chosenPoints = [];

      _.each(list, point => {
        let tooClose = false;

        _.each(chosenPoints, point2 => {
          let p1 = turf.point([
              _.get(point, 'coord.Lon'),
              _.get(point, 'coord.Lat'),
            ]),
            p2 = turf.point([
              _.get(point2, 'coord.Lon'),
              _.get(point2, 'coord.Lat'),
            ]);

          if (turf.distance(p1, p2, { units: 'miles' }) < maxDistance) {
            tooClose = true;
            return false;
          }
        });

        if (!tooClose) {
          chosenPoints.push(point);
        }
      });

      this.log('weather | got on chosen points');
      _.each(chosenPoints, d => {
        const coords = [_.get(d, 'coord.Lon'), _.get(d, 'coord.Lat')],
          marker = document.createElement('span'),
          main = _.get(d, 'weather.0.main'),
          icon = _.get(d, 'weather.0.icon');

        if (icon === '01d' || icon === '01n') {
          marker.innerHTML = `
            <img
            src="https://openweathermap.org/img/w/${icon}.png"
            style="width: 60px; height: auto"
            />
          `;
        } else {
          marker.innerHTML = `
            <img
            src="https://openweathermap.org/img/wn/${icon}@2x.png"
            style="width: 60px; height: auto"
            />
          `;
        }

        const m = new mapboxgl.Marker({
          element: marker,
          anchor: 'center',
        })
          .setLngLat(coords)
          .addTo(map);

        $(m._element).click(() => {
          this.log('opening weather', d);
          this.tripService.tools.openWeatherLocation(d);
        });

        this.weatherMarkers.push(m);
      });
    });
  }

  private distanceBetweenWeatherIcons(route, maxDistance) {
    const start = _.head(_.get(route, 'geometry.coordinates'));
    const stop = _.last(_.get(route, 'geometry.coordinates'));
    const startPoint = turf.point(_.toArray(start)),
      stopPoint = turf.point(_.toArray(stop));
    const distance = turf.distance(startPoint, stopPoint, {
      units: 'miles',
    });
    // max locations
    this.log('weather | miles', distance);
    this.log('weather | distance between', _.toInteger(distance / 4));
    return _.min([_.toInteger(distance / 4), maxDistance]);
  }
}



// WEBPACK FOOTER //
// ./src/providers/trip-service/drawing-tools.ts